import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { ProductCard } from '@/components/ProductCard';
import { ProductSkeleton } from '@/components/Skeletons';
import { getCategories, getProducts, getProductsByCategory } from '@/lib/api';

const PRODUCT_LIMIT = 200;

export default function ProductsPage() {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load categories once
  useEffect(() => {
    getCategories()
      .then((res) => setCategories(res.data))
      .catch((err) => console.error('Categories fetch error:', err));
  }, []);

  // Refetch products whenever the URL (categorySlug or ?search=) changes.
  // The URL is the SINGLE SOURCE OF TRUTH — this kills the stale-filter bug.
  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let data = [];
        let cat = null;
        if (categorySlug) {
          const res = await getProductsByCategory(categorySlug);
          data = res.data.products || [];
          cat = res.data.category || null;
        } else if (searchQuery) {
          const res = await getProducts({ search: searchQuery, limit: PRODUCT_LIMIT });
          data = res.data;
        } else {
          const res = await getProducts({ limit: PRODUCT_LIMIT });
          data = res.data;
        }
        if (!cancelled) {
          setProducts(data);
          setCurrentCategory(cat);
        }
      } catch (err) {
        console.error('Products fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => { cancelled = true; };
  }, [categorySlug, searchQuery]);

  // All filter changes go through URL navigation — guarantees state stays in sync.
  const handleCategoryFilter = (slug) => {
    if (!slug || slug === categorySlug) {
      navigate('/products'); // clears category AND any search param
    } else {
      navigate(`/products/${slug}`); // sets category, drops search param
    }
  };

  const clearSearch = () => navigate('/products');

  return (
    <CustomerLayout>
      <div className="px-4 py-4 max-w-7xl mx-auto" data-testid="products-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4">
          <Link to="/" className="text-muted-foreground hover:text-foreground">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">
            {searchQuery
              ? `Search: "${searchQuery}"`
              : currentCategory
                ? currentCategory.name
                : 'All Products'}
          </span>
          <span className="ml-auto text-sm text-muted-foreground" data-testid="products-count">
            {loading ? '' : `${products.length} items`}
          </span>
        </div>

        {/* Category chips */}
        <div className="mb-6 -mx-4 px-4">
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
            <Button
              variant={!categorySlug && !searchQuery ? 'default' : 'outline'}
              size="sm"
              className="rounded-full flex-shrink-0 h-10 px-5 font-semibold"
              onClick={() => handleCategoryFilter(null)}
              data-testid="filter-all"
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={categorySlug === cat.slug ? 'default' : 'outline'}
                size="sm"
                className="rounded-full flex-shrink-0 h-10 px-5 font-semibold"
                onClick={() => handleCategoryFilter(cat.slug)}
                data-testid={`filter-${cat.slug}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Active filter pills */}
        {(categorySlug || searchQuery) && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {categorySlug && currentCategory && (
              <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-semibold">
                {currentCategory.name}
                <button
                  onClick={() => handleCategoryFilter(null)}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                  data-testid="clear-category-filter"
                  aria-label="Clear category filter"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-1 bg-accent/20 text-accent-foreground px-3 py-1 rounded-full text-sm font-semibold">
                &quot;{searchQuery}&quot;
                <button
                  onClick={clearSearch}
                  className="hover:bg-accent/30 rounded-full p-0.5"
                  data-testid="clear-search-filter"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Products Grid */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
          data-testid="products-grid"
        >
          {loading ? (
            Array.from({ length: 10 }).map((_, i) => <ProductSkeleton key={`sk-${i}`} />)
          ) : products.length > 0 ? (
            products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">No products found</p>
              <Link to="/products">
                <Button variant="outline" className="mt-4 rounded-full">
                  Browse All Products
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}
